// @ts-nocheck
/**
 * IoT/Telecom Industry Configuration
 *
 * Comprehensive configuration for SIM management, network optimization,
 * and IoT fleet operations. Supports multi-carrier connectivity management,
 * signal analytics, and regulatory compliance tracking.
 *
 * Key Features:
 * - Multi-IMSI eUICC SIM management
 * - Real-time signal quality monitoring
 * - Carrier profile optimization
 * - Global regulatory compliance
 * - Predictive connectivity analytics
 */

import { IndustryConfig } from '../industryTypes'

export const iotTelecomConfig: IndustryConfig = {
  id: 'iot_telecom',
  name: 'IoT/Telecom',
  description: 'SIM management and network optimization for IoT deployments',
  icon: 'Radio',

  terminology: {
    asset: 'SIM',
    assets: 'SIMs',
    assetPlural: 'SIMs',
    assetType: 'Device Type',
    failure: 'Connectivity Loss',
    health: 'Signal Health',
    healthScore: 'Signal Score',
    healthy: 'Connected',
    degraded: 'Weak Signal',
    critical: 'Offline',
    alert: 'Network Alert',
    process: 'Data Session',
    maintenance: 'Profile Management',
    inspection: 'Diagnostic Check',
    downtime: 'Disconnection',
    workOrder: 'Network Ticket',
    prediction: 'Signal Forecast',
    anomaly: 'Network Anomaly',
    trend: 'Usage Trend',
    risk: 'Coverage Risk',
    facility: 'Network Region',
    facilityPlural: 'Network Regions',
    location: 'Cell Tower',
    operator: 'NOC Engineer',
    supervisor: 'Network Operations Manager',
    calibration: 'Profile Sync',
    compliance: 'Regulatory Compliance',
    criticality: 'SIM Priority',
  },

  assetTypes: [
    {
      id: 'sim_card',
      name: 'SIM Card',
      icon: 'CreditCard',
      description: 'Multi-IMSI eUICC SIM cards for global IoT connectivity',
      category: 'Connectivity',
      defaultMetrics: ['rssi', 'rsrp', 'latency', 'data_usage'],
      maintenanceTypes: ['profile_switch', 'ota_update', 'diagnostic', 'suspension'],
      criticalityLevels: [
        { id: 'mission_critical', name: 'Mission Critical', color: '#ef4444', description: 'Life-safety or revenue-critical', downtimeCostMultiplier: 100 },
        { id: 'high', name: 'High Priority', color: '#f59e0b', description: 'Critical business operations', downtimeCostMultiplier: 50 },
        { id: 'standard', name: 'Standard', color: '#3b82f6', description: 'Normal operations', downtimeCostMultiplier: 10 },
        { id: 'low', name: 'Low Priority', color: '#6b7280', description: 'Non-critical monitoring', downtimeCostMultiplier: 1 },
      ],
    },
    {
      id: 'network_profile',
      name: 'Network Profile',
      icon: 'Wifi',
      description: 'Carrier-specific network configurations and APNs',
      category: 'Configuration',
      defaultMetrics: ['activation_success', 'switch_latency', 'coverage_score'],
      maintenanceTypes: ['profile_update', 'carrier_change', 'apn_config'],
      criticalityLevels: [
        { id: 'primary', name: 'Primary', color: '#22c55e', description: 'Default carrier profile', downtimeCostMultiplier: 20 },
        { id: 'fallback', name: 'Fallback', color: '#3b82f6', description: 'Backup carrier profile', downtimeCostMultiplier: 5 },
        { id: 'roaming', name: 'Roaming', color: '#f59e0b', description: 'International roaming', downtimeCostMultiplier: 10 },
      ],
    },
    {
      id: 'iot_gateway',
      name: 'IoT Gateway',
      icon: 'Router',
      description: 'Edge devices aggregating multiple SIM connections',
      category: 'Infrastructure',
      defaultMetrics: ['connected_sims', 'throughput', 'uptime', 'cpu_usage'],
      maintenanceTypes: ['firmware_update', 'config_change', 'restart', 'replacement'],
      criticalityLevels: [
        { id: 'critical', name: 'Critical', color: '#ef4444', description: 'Primary gateway', downtimeCostMultiplier: 200 },
        { id: 'high', name: 'High', color: '#f59e0b', description: 'Secondary gateway', downtimeCostMultiplier: 50 },
        { id: 'standard', name: 'Standard', color: '#3b82f6', description: 'Remote gateway', downtimeCostMultiplier: 10 },
      ],
    },
    {
      id: 'cellular_modem',
      name: 'Cellular Modem',
      icon: 'Smartphone',
      description: 'Embedded cellular modems in IoT devices',
      category: 'Hardware',
      defaultMetrics: ['rssi', 'connection_state', 'data_rate', 'temperature'],
      maintenanceTypes: ['firmware_update', 'antenna_check', 'reset', 'replacement'],
      criticalityLevels: [
        { id: 'critical', name: 'Critical', color: '#ef4444', description: 'Production-critical modem', downtimeCostMultiplier: 150 },
        { id: 'high', name: 'High', color: '#f59e0b', description: 'Business-critical', downtimeCostMultiplier: 75 },
        { id: 'standard', name: 'Standard', color: '#3b82f6', description: 'Standard deployment', downtimeCostMultiplier: 15 },
        { id: 'low', name: 'Low', color: '#6b7280', description: 'Test/Development', downtimeCostMultiplier: 2 },
      ],
    },
    {
      id: 'esim_profile',
      name: 'eSIM Profile',
      icon: 'Layers',
      description: 'Downloadable carrier profiles for embedded SIMs',
      category: 'Configuration',
      defaultMetrics: ['profile_state', 'activation_date', 'usage', 'carrier'],
      maintenanceTypes: ['profile_download', 'profile_enable', 'profile_delete', 'profile_switch'],
      criticalityLevels: [
        { id: 'active', name: 'Active', color: '#22c55e', description: 'Currently enabled profile', downtimeCostMultiplier: 25 },
        { id: 'standby', name: 'Standby', color: '#3b82f6', description: 'Available for activation', downtimeCostMultiplier: 5 },
        { id: 'expired', name: 'Expired', color: '#6b7280', description: 'Needs renewal', downtimeCostMultiplier: 1 },
      ],
    },
  ],

  kpiDomains: [
    {
      id: 'signal_quality',
      name: 'Signal Quality',
      icon: 'Signal',
      description: 'RF signal strength and quality metrics',
      businessValue: 'Ensure reliable connectivity and optimal network selection',
      category: 'industry_specific',
      metrics: [
        { id: 'rssi', name: 'RSSI', unit: 'dBm', description: 'Received Signal Strength Indicator', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: -70, warning: -85, critical: -100 } },
        { id: 'rsrp', name: 'RSRP', unit: 'dBm', description: 'Reference Signal Received Power', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: -80, warning: -100, critical: -120 } },
        { id: 'rsrq', name: 'RSRQ', unit: 'dB', description: 'Reference Signal Received Quality', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: -10, warning: -15, critical: -20 } },
        { id: 'sinr', name: 'SINR', unit: 'dB', description: 'Signal to Interference plus Noise Ratio', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 10, warning: 0, critical: -5 } },
      ],
      thresholds: { healthy: { min: -70 }, warning: { min: -100, max: -70 }, critical: { max: -100 } },
      visualizations: ['gauge', 'line_chart', 'heatmap', 'distribution'],
    },
    {
      id: 'network_performance',
      name: 'Network Performance',
      icon: 'Activity',
      description: 'Latency, throughput, and reliability metrics',
      businessValue: 'Optimize data transfer and minimize packet loss',
      category: 'universal',
      metrics: [
        { id: 'latency', name: 'Round-Trip Latency', unit: 'ms', description: 'Network round-trip time', dataType: 'continuous', aggregation: 'avg', goodDirection: 'lower', defaultThresholds: { healthy: 50, warning: 150, critical: 300 } },
        { id: 'jitter', name: 'Jitter', unit: 'ms', description: 'Latency variation', dataType: 'continuous', aggregation: 'avg', goodDirection: 'lower', defaultThresholds: { healthy: 10, warning: 30, critical: 50 } },
        { id: 'packet_loss', name: 'Packet Loss', unit: '%', description: 'Percentage of lost packets', dataType: 'continuous', aggregation: 'avg', goodDirection: 'lower', defaultThresholds: { healthy: 0.5, warning: 2, critical: 5 } },
        { id: 'throughput', name: 'Throughput', unit: 'Mbps', description: 'Data transfer rate', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 10, warning: 2, critical: 0.5 } },
      ],
      thresholds: { healthy: { max: 50 }, warning: { min: 50, max: 150 }, critical: { min: 150 } },
      visualizations: ['line_chart', 'gauge', 'comparison', 'trend'],
    },
    {
      id: 'data_usage',
      name: 'Data Usage',
      icon: 'BarChart2',
      description: 'Data consumption and quota management',
      businessValue: 'Control costs and prevent overages',
      category: 'industry_specific',
      metrics: [
        { id: 'data_consumed', name: 'Data Consumed', unit: 'MB', description: 'Total data used', dataType: 'continuous', aggregation: 'sum', goodDirection: 'target', defaultThresholds: { healthy: 500, warning: 800, critical: 1000 } },
        { id: 'quota_used', name: 'Quota Used', unit: '%', description: 'Percentage of data quota used', dataType: 'continuous', aggregation: 'latest', goodDirection: 'lower', defaultThresholds: { healthy: 70, warning: 90, critical: 100 } },
        { id: 'daily_average', name: 'Daily Average', unit: 'MB', description: 'Average daily consumption', dataType: 'continuous', aggregation: 'avg', goodDirection: 'target', defaultThresholds: { healthy: 20, warning: 40, critical: 60 } },
      ],
      thresholds: { healthy: { max: 70 }, warning: { min: 70, max: 90 }, critical: { min: 90 } },
      visualizations: ['bar_chart', 'trend', 'distribution', 'comparison'],
    },
    {
      id: 'connectivity_status',
      name: 'Connectivity Status',
      icon: 'Wifi',
      description: 'Connection state and registration status',
      businessValue: 'Maintain high availability and rapid issue detection',
      category: 'universal',
      metrics: [
        { id: 'connection_uptime', name: 'Connection Uptime', unit: '%', description: 'Percentage of time connected', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 99.5, warning: 98, critical: 95 } },
        { id: 'registration_success', name: 'Registration Success', unit: '%', description: 'Network registration success rate', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 99, warning: 95, critical: 90 } },
        { id: 'handover_success', name: 'Handover Success', unit: '%', description: 'Cell handover success rate', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 98, warning: 95, critical: 90 } },
      ],
      thresholds: { healthy: { min: 99 }, warning: { min: 95, max: 99 }, critical: { max: 95 } },
      visualizations: ['gauge', 'line_chart', 'heatmap', 'comparison'],
    },
    {
      id: 'cost_management',
      name: 'Cost Management',
      icon: 'DollarSign',
      description: 'Connectivity costs and budget tracking',
      businessValue: 'Optimize spend and prevent cost overruns',
      category: 'industry_specific',
      metrics: [
        { id: 'monthly_cost', name: 'Monthly Cost', unit: 'USD', description: 'Total monthly connectivity spend', dataType: 'continuous', aggregation: 'sum', goodDirection: 'lower', defaultThresholds: { healthy: 1000, warning: 1500, critical: 2000 } },
        { id: 'cost_per_sim', name: 'Cost per SIM', unit: 'USD', description: 'Average cost per active SIM', dataType: 'continuous', aggregation: 'avg', goodDirection: 'lower', defaultThresholds: { healthy: 5, warning: 10, critical: 20 } },
        { id: 'overage_charges', name: 'Overage Charges', unit: 'USD', description: 'Charges for exceeding data limits', dataType: 'continuous', aggregation: 'sum', goodDirection: 'lower', defaultThresholds: { healthy: 0, warning: 100, critical: 500 } },
        { id: 'roaming_cost', name: 'Roaming Cost', unit: 'USD', description: 'International roaming charges', dataType: 'continuous', aggregation: 'sum', goodDirection: 'lower', defaultThresholds: { healthy: 50, warning: 200, critical: 500 } },
      ],
      thresholds: { healthy: { max: 1000 }, warning: { min: 1000, max: 1500 }, critical: { min: 1500 } },
      visualizations: ['bar_chart', 'trend', 'comparison', 'distribution'],
    },
    {
      id: 'fleet_management',
      name: 'Fleet Management',
      icon: 'LayoutGrid',
      description: 'SIM fleet composition and lifecycle status',
      businessValue: 'Maintain optimal fleet size and lifecycle management',
      category: 'industry_specific',
      metrics: [
        { id: 'active_sims', name: 'Active SIMs', unit: 'count', description: 'Number of active SIM cards', dataType: 'discrete', aggregation: 'count', goodDirection: 'target', defaultThresholds: { healthy: 100, warning: 50, critical: 10 } },
        { id: 'suspended_sims', name: 'Suspended SIMs', unit: 'count', description: 'Number of suspended SIM cards', dataType: 'discrete', aggregation: 'count', goodDirection: 'lower', defaultThresholds: { healthy: 5, warning: 20, critical: 50 } },
        { id: 'activation_rate', name: 'Activation Rate', unit: '%', description: 'Rate of successful activations', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 98, warning: 90, critical: 80 } },
        { id: 'churn_rate', name: 'Churn Rate', unit: '%', description: 'Rate of SIM deactivations', dataType: 'continuous', aggregation: 'avg', goodDirection: 'lower', defaultThresholds: { healthy: 2, warning: 5, critical: 10 } },
      ],
      thresholds: { healthy: { min: 98 }, warning: { min: 90, max: 98 }, critical: { max: 90 } },
      visualizations: ['gauge', 'bar_chart', 'trend', 'distribution'],
    },
  ],

  maintenanceTypes: [
    {
      id: 'profile_switch',
      name: 'Profile Switch',
      description: 'Switch to a different carrier profile for better coverage',
      category: 'condition_based',
      defaultInterval: { value: 0, unit: 'hours' },
      laborRequirements: [{ role: 'noc_engineer', count: 1, hourlyRate: 75 }],
      estimatedDuration: { min: 1, typical: 5, max: 15, unit: 'minutes' },
      skillsRequired: ['network_management', 'carrier_configs'],
      partsCommonly: [],
    },
    {
      id: 'ota_update',
      name: 'OTA Update',
      description: 'Over-the-air profile or firmware update',
      category: 'preventive',
      defaultInterval: { value: 30, unit: 'days' },
      laborRequirements: [{ role: 'noc_engineer', count: 1, hourlyRate: 75 }],
      estimatedDuration: { min: 5, typical: 15, max: 60, unit: 'minutes' },
      skillsRequired: ['ota_management', 'firmware'],
      partsCommonly: [],
    },
    {
      id: 'diagnostic',
      name: 'Diagnostic Check',
      description: 'Run full diagnostic on SIM and network connection',
      category: 'condition_based',
      defaultInterval: { value: 0, unit: 'hours' },
      laborRequirements: [{ role: 'noc_engineer', count: 1, hourlyRate: 75 }],
      estimatedDuration: { min: 5, typical: 15, max: 30, unit: 'minutes' },
      skillsRequired: ['troubleshooting', 'network_diagnostics'],
      partsCommonly: [],
    },
    {
      id: 'sim_replacement',
      name: 'SIM Replacement',
      description: 'Physical SIM card replacement',
      category: 'corrective',
      laborRequirements: [{ role: 'field_technician', count: 1, hourlyRate: 50 }],
      estimatedDuration: { min: 15, typical: 30, max: 60, unit: 'minutes' },
      skillsRequired: ['field_service', 'sim_provisioning'],
      partsCommonly: ['euicc_sim', 'sim_adapter'],
    },
    {
      id: 'antenna_optimization',
      name: 'Antenna Optimization',
      description: 'Optimize or replace antenna for better signal',
      category: 'condition_based',
      laborRequirements: [{ role: 'field_technician', count: 1, hourlyRate: 50 }],
      estimatedDuration: { min: 30, typical: 60, max: 120, unit: 'minutes' },
      skillsRequired: ['rf_engineering', 'antenna_installation'],
      partsCommonly: ['external_antenna', 'antenna_cable', 'mounting_bracket'],
    },
    {
      id: 'gateway_firmware',
      name: 'Gateway Firmware Update',
      description: 'Update IoT gateway firmware for new features or security patches',
      category: 'preventive',
      defaultInterval: { value: 90, unit: 'days' },
      laborRequirements: [{ role: 'noc_engineer', count: 1, hourlyRate: 75 }],
      estimatedDuration: { min: 15, typical: 30, max: 60, unit: 'minutes' },
      skillsRequired: ['gateway_management', 'firmware'],
      partsCommonly: [],
    },
    {
      id: 'carrier_migration',
      name: 'Carrier Migration',
      description: 'Migrate SIM fleet to new carrier or profile set',
      category: 'preventive',
      laborRequirements: [{ role: 'noc_engineer', count: 2, hourlyRate: 75 }, { role: 'fleet_manager', count: 1, hourlyRate: 100 }],
      estimatedDuration: { min: 120, typical: 480, max: 1440, unit: 'minutes' },
      skillsRequired: ['carrier_management', 'bulk_operations', 'project_management'],
      partsCommonly: [],
    },
    {
      id: 'compliance_audit',
      name: 'Compliance Audit',
      description: 'Verify regulatory compliance across fleet',
      category: 'preventive',
      defaultInterval: { value: 90, unit: 'days' },
      laborRequirements: [{ role: 'fleet_manager', count: 1, hourlyRate: 100 }],
      estimatedDuration: { min: 60, typical: 240, max: 480, unit: 'minutes' },
      skillsRequired: ['regulatory_compliance', 'audit'],
      partsCommonly: [],
    },
  ],

  riskFactors: [
    {
      id: 'signal_degradation',
      name: 'Signal Degradation',
      description: 'Risk of weak signal affecting connectivity',
      category: 'operational',
      weight: 0.25,
      calculationMethod: 'threshold',
      inputs: ['rssi', 'rsrp', 'rsrq'],
    },
    {
      id: 'coverage_gap',
      name: 'Coverage Gap',
      description: 'Risk of no coverage in deployment area',
      category: 'operational',
      weight: 0.20,
      calculationMethod: 'linear',
      inputs: ['coverage_score', 'carrier_count'],
    },
    {
      id: 'cost_overrun',
      name: 'Cost Overrun',
      description: 'Risk of exceeding data budget',
      category: 'financial',
      weight: 0.15,
      calculationMethod: 'threshold',
      inputs: ['quota_used', 'cost_trend'],
    },
    {
      id: 'regulatory_non_compliance',
      name: 'Regulatory Non-Compliance',
      description: 'Risk of violating local telecom regulations',
      category: 'compliance',
      weight: 0.15,
      calculationMethod: 'threshold',
      inputs: ['local_imsi_required', 'registration_status'],
    },
    {
      id: 'hardware_failure',
      name: 'Hardware Failure',
      description: 'Risk of SIM or gateway hardware failure',
      category: 'operational',
      weight: 0.10,
      calculationMethod: 'threshold',
      inputs: ['device_age', 'error_rate', 'temperature'],
    },
    {
      id: 'security_breach',
      name: 'Security Breach',
      description: 'Risk of SIM fraud or unauthorized access',
      category: 'safety',
      weight: 0.10,
      calculationMethod: 'threshold',
      inputs: ['unusual_activity', 'location_anomaly', 'usage_spike'],
    },
    {
      id: 'carrier_outage',
      name: 'Carrier Outage',
      description: 'Risk of carrier network outage affecting connectivity',
      category: 'operational',
      weight: 0.05,
      calculationMethod: 'threshold',
      inputs: ['carrier_status', 'redundancy_level'],
    },
  ],

  complianceStandards: [
    {
      id: 'brazil_anatel',
      name: 'ANATEL Brazil',
      shortName: 'ANATEL',
      description: 'Brazilian telecom regulatory requirements',
      authority: 'ANATEL',
      region: ['BR'],
      requirements: [
        { id: 'local_imsi', name: 'Local IMSI Required', description: 'Must use Brazil-issued IMSI', kpiId: 'local_imsi', threshold: 1, operator: 'eq', severity: 'critical' },
        { id: 'data_sovereignty', name: 'Data Sovereignty', description: 'Data must be processed locally', kpiId: 'data_location', threshold: 1, operator: 'eq', severity: 'required' },
      ],
      reportingFrequency: 'monthly',
    },
    {
      id: 'china_miit',
      name: 'MIIT China',
      shortName: 'MIIT',
      description: 'Chinese telecom regulatory requirements',
      authority: 'MIIT',
      region: ['CN'],
      requirements: [
        { id: 'local_imsi', name: 'Local IMSI Required', description: 'Must use China-issued IMSI', kpiId: 'local_imsi', threshold: 1, operator: 'eq', severity: 'critical' },
        { id: 'registration', name: 'Real-Name Registration', description: 'SIM must be registered to entity', kpiId: 'registration_status', threshold: 1, operator: 'eq', severity: 'critical' },
      ],
      reportingFrequency: 'monthly',
    },
    {
      id: 'usa_fcc',
      name: 'FCC United States',
      shortName: 'FCC',
      description: 'US Federal Communications Commission requirements',
      authority: 'FCC',
      region: ['US'],
      requirements: [
        { id: 'e911', name: 'E911 Compliance', description: 'Emergency services location capability', kpiId: 'e911_enabled', threshold: 1, operator: 'eq', severity: 'critical' },
        { id: 'device_certification', name: 'Device Certification', description: 'FCC certified equipment only', kpiId: 'fcc_certified', threshold: 1, operator: 'eq', severity: 'required' },
      ],
      reportingFrequency: 'quarterly',
    },
    {
      id: 'eu_gdpr',
      name: 'GDPR European Union',
      shortName: 'GDPR',
      description: 'EU data protection and privacy requirements',
      authority: 'European Commission',
      region: ['EU', 'EEA'],
      requirements: [
        { id: 'data_privacy', name: 'Data Privacy', description: 'Personal data protection compliance', kpiId: 'gdpr_compliant', threshold: 1, operator: 'eq', severity: 'critical' },
        { id: 'data_retention', name: 'Data Retention', description: 'Limit data retention periods', kpiId: 'retention_compliant', threshold: 1, operator: 'eq', severity: 'required' },
      ],
      reportingFrequency: 'annual',
    },
    {
      id: 'india_trai',
      name: 'TRAI India',
      shortName: 'TRAI',
      description: 'Telecom Regulatory Authority of India requirements',
      authority: 'TRAI',
      region: ['IN'],
      requirements: [
        { id: 'local_imsi', name: 'Local IMSI Required', description: 'Must use India-issued IMSI', kpiId: 'local_imsi', threshold: 1, operator: 'eq', severity: 'critical' },
        { id: 'kyc', name: 'KYC Verification', description: 'Know Your Customer compliance', kpiId: 'kyc_verified', threshold: 1, operator: 'eq', severity: 'critical' },
      ],
      reportingFrequency: 'monthly',
    },
  ],

  professionalRoles: [
    {
      id: 'noc_engineer',
      category: 'technician',
      name: 'NOC Engineer',
      shortName: 'NOC',
      description: 'Network Operations Center engineer monitoring SIM fleet',
      responsibilities: ['Monitor SIM connectivity', 'Troubleshoot network issues', 'Perform profile switches', 'Manage OTA updates'],
      focusAreas: ['Signal metrics', 'Connectivity status', 'Network performance'],
      explanationStyle: {
        depth: 'technical',
        includeMetrics: true,
        includeCosts: false,
        includeRootCause: true,
        includeProcedures: true,
        includeTimelines: true,
        vocabularyLevel: 'technical',
        focusOn: ['technical_details', 'action_items'],
      },
      decisionAuthority: ['Profile switches', 'Diagnostic runs', 'OTA scheduling'],
      reportingTo: 'Network Operations Manager',
      icon: 'Monitor',
    },
    {
      id: 'field_technician',
      category: 'technician',
      name: 'Field Technician',
      shortName: 'FT',
      description: 'On-site technician for physical installations and repairs',
      responsibilities: ['SIM installations', 'Antenna deployments', 'Hardware replacements', 'Signal testing'],
      focusAreas: ['Physical components', 'Signal strength', 'Installation quality'],
      explanationStyle: {
        depth: 'technical',
        includeMetrics: true,
        includeCosts: false,
        includeRootCause: true,
        includeProcedures: true,
        includeTimelines: true,
        vocabularyLevel: 'technical',
        focusOn: ['technical_details', 'action_items'],
      },
      decisionAuthority: ['Component replacements', 'Antenna positioning', 'On-site diagnostics'],
      reportingTo: 'NOC Engineer',
      icon: 'Wrench',
    },
    {
      id: 'iot_connectivity_engineer',
      category: 'sme',
      name: 'IoT Connectivity Engineer',
      shortName: 'ICE',
      description: 'Senior SME for the IoT / eSIM connectivity estate: profile lifecycle, carrier mix, coverage strategy',
      responsibilities: ['Connectivity estate health monitoring', 'Cost / data-pool optimization', 'Carrier + MNO negotiations', 'eSIM profile deployment planning'],
      focusAreas: ['Connectivity overview', 'Data-pool / ARPU metrics', 'Coverage analysis', 'Profile lifecycle'],
      explanationStyle: {
        depth: 'detailed',
        includeMetrics: true,
        includeCosts: true,
        includeRootCause: true,
        includeProcedures: false,
        includeTimelines: true,
        vocabularyLevel: 'professional',
        focusOn: ['business_impact', 'action_items', 'risk'],
      },
      decisionAuthority: ['Carrier selection', 'Budget allocation', 'Fleet scaling'],
      reportingTo: 'VP Operations',
      icon: 'Users',
    },
    {
      id: 'compliance_analyst',
      category: 'analyst',
      name: 'Compliance Analyst',
      shortName: 'CA',
      description: 'Ensures regulatory compliance across all regions',
      responsibilities: ['Regulatory monitoring', 'Compliance reporting', 'Audit preparation', 'Policy enforcement'],
      focusAreas: ['Compliance status', 'Regional requirements', 'Audit findings'],
      explanationStyle: {
        depth: 'detailed',
        includeMetrics: true,
        includeCosts: false,
        includeRootCause: true,
        includeProcedures: true,
        includeTimelines: true,
        vocabularyLevel: 'professional',
        focusOn: ['compliance', 'risk', 'action_items'],
      },
      decisionAuthority: ['Compliance enforcement', 'Audit scheduling', 'Policy updates'],
      reportingTo: 'Fleet Manager',
      icon: 'Shield',
    },
    {
      id: 'executive',
      category: 'executive',
      name: 'VP Operations',
      shortName: 'VP',
      description: 'Executive oversight of IoT operations',
      responsibilities: ['Strategic direction', 'Budget approval', 'Partner relationships', 'Risk management'],
      focusAreas: ['Executive metrics', 'Cost trends', 'Compliance status'],
      explanationStyle: {
        depth: 'high_level',
        includeMetrics: false,
        includeCosts: true,
        includeRootCause: false,
        includeProcedures: false,
        includeTimelines: true,
        vocabularyLevel: 'executive',
        focusOn: ['business_impact', 'risk'],
      },
      decisionAuthority: ['Budget approval', 'Strategic partnerships', 'Major initiatives'],
      reportingTo: 'CEO',
      icon: 'Briefcase',
    },
  ],

  partsCatalog: {
    categories: [
      {
        id: 'sim_components',
        name: 'SIM Components',
        description: 'Physical SIM cards and related components',
        parts: [
          {
            id: 'euicc_sim',
            partNumber: 'SIM-EUICC-001',
            name: 'eUICC SIM Card',
            description: 'Programmable embedded SIM with multiple profile support',
            category: 'sim_components',
            manufacturer: 'floLIVE',
            unitCost: 5.00,
            currency: 'USD',
            leadTime: { value: 5, unit: 'days' },
            criticalityLevel: 'critical',
            compatibleAssetTypes: ['sim_card'],
            failureModes: ['hardware_failure', 'profile_corruption'],
            averageLifespan: { value: 10, unit: 'years' },
          },
          {
            id: 'standard_sim',
            partNumber: 'SIM-STD-001',
            name: 'Standard SIM Card',
            description: 'Single-profile SIM for fixed carrier deployment',
            category: 'sim_components',
            manufacturer: 'Various',
            unitCost: 2.00,
            currency: 'USD',
            leadTime: { value: 3, unit: 'days' },
            criticalityLevel: 'standard',
            compatibleAssetTypes: ['sim_card'],
            failureModes: ['hardware_failure'],
            averageLifespan: { value: 5, unit: 'years' },
          },
          {
            id: 'sim_adapter',
            partNumber: 'SIM-ADP-001',
            name: 'SIM Adapter Kit',
            description: 'Nano to micro to standard SIM adapter set',
            category: 'sim_components',
            manufacturer: 'Generic',
            unitCost: 1.00,
            currency: 'USD',
            leadTime: { value: 2, unit: 'days' },
            criticalityLevel: 'optional',
            compatibleAssetTypes: ['sim_card'],
            failureModes: ['physical_damage'],
            averageLifespan: { value: 3, unit: 'years' },
          },
          {
            id: 'sim_tray',
            partNumber: 'SIM-TRAY-001',
            name: 'SIM Card Tray',
            description: 'Replacement SIM card tray for various devices',
            category: 'sim_components',
            manufacturer: 'Various',
            unitCost: 3.00,
            currency: 'USD',
            leadTime: { value: 3, unit: 'days' },
            criticalityLevel: 'standard',
            compatibleAssetTypes: ['cellular_modem', 'iot_gateway'],
            failureModes: ['physical_damage', 'wear'],
          },
          {
            id: 'sim_extender',
            partNumber: 'SIM-EXT-001',
            name: 'SIM Card Extender Cable',
            description: 'Flexible extender for external SIM mounting',
            category: 'sim_components',
            manufacturer: 'RF Solutions',
            unitCost: 15.00,
            currency: 'USD',
            leadTime: { value: 5, unit: 'days' },
            criticalityLevel: 'optional',
            compatibleAssetTypes: ['cellular_modem', 'iot_gateway'],
            failureModes: ['cable_break', 'connector_wear'],
            averageLifespan: { value: 2, unit: 'years' },
          },
        ],
      },
      {
        id: 'antenna_equipment',
        name: 'Antenna Equipment',
        description: 'External antennas and RF components',
        parts: [
          {
            id: 'external_antenna',
            partNumber: 'ANT-EXT-001',
            name: 'External LTE Antenna',
            description: 'High-gain external antenna for improved signal',
            category: 'antenna_equipment',
            manufacturer: 'Taoglas',
            unitCost: 45.00,
            currency: 'USD',
            leadTime: { value: 7, unit: 'days' },
            criticalityLevel: 'essential',
            compatibleAssetTypes: ['cellular_modem', 'iot_gateway'],
            failureModes: ['physical_damage', 'water_ingress', 'connector_corrosion'],
            averageLifespan: { value: 5, unit: 'years' },
            specifications: { gain: '5 dBi', frequency: '698-2700 MHz', connector: 'SMA Male' },
          },
          {
            id: 'mimo_antenna',
            partNumber: 'ANT-MIMO-001',
            name: 'MIMO Antenna Assembly',
            description: 'Dual-element MIMO antenna for LTE/5G',
            category: 'antenna_equipment',
            manufacturer: 'Taoglas',
            unitCost: 85.00,
            currency: 'USD',
            leadTime: { value: 10, unit: 'days' },
            criticalityLevel: 'essential',
            compatibleAssetTypes: ['iot_gateway'],
            failureModes: ['physical_damage', 'element_failure'],
            averageLifespan: { value: 5, unit: 'years' },
            specifications: { gain: '6 dBi', frequency: '617-6000 MHz', ports: 2 },
          },
          {
            id: 'antenna_cable',
            partNumber: 'ANT-CBL-001',
            name: 'Low-Loss Antenna Cable',
            description: 'LMR-200 coaxial cable for antenna connection',
            category: 'antenna_equipment',
            manufacturer: 'Times Microwave',
            unitCost: 25.00,
            currency: 'USD',
            leadTime: { value: 3, unit: 'days' },
            criticalityLevel: 'standard',
            compatibleAssetTypes: ['cellular_modem', 'iot_gateway'],
            failureModes: ['cable_damage', 'connector_wear'],
            averageLifespan: { value: 7, unit: 'years' },
            specifications: { length: '3m', loss: '0.6 dB/m @ 2.4 GHz' },
          },
          {
            id: 'mounting_bracket',
            partNumber: 'ANT-MNT-001',
            name: 'Antenna Mounting Bracket',
            description: 'Universal pole/wall mount for external antennas',
            category: 'antenna_equipment',
            manufacturer: 'Generic',
            unitCost: 12.00,
            currency: 'USD',
            leadTime: { value: 3, unit: 'days' },
            criticalityLevel: 'optional',
            compatibleAssetTypes: ['cellular_modem', 'iot_gateway'],
            failureModes: ['corrosion', 'mechanical_failure'],
            averageLifespan: { value: 10, unit: 'years' },
          },
          {
            id: 'lightning_arrestor',
            partNumber: 'ANT-LA-001',
            name: 'Lightning Arrestor',
            description: 'Surge protection for outdoor antenna installations',
            category: 'antenna_equipment',
            manufacturer: 'Polyphaser',
            unitCost: 65.00,
            currency: 'USD',
            leadTime: { value: 7, unit: 'days' },
            criticalityLevel: 'essential',
            compatibleAssetTypes: ['iot_gateway'],
            failureModes: ['surge_damage', 'wear'],
            averageLifespan: { value: 5, unit: 'years' },
          },
        ],
      },
      {
        id: 'gateway_components',
        name: 'Gateway Components',
        description: 'IoT gateway hardware and accessories',
        parts: [
          {
            id: 'gateway_power_supply',
            partNumber: 'GW-PWR-001',
            name: 'Gateway Power Supply',
            description: '12V DC power adapter for IoT gateways',
            category: 'gateway_components',
            manufacturer: 'Mean Well',
            unitCost: 25.00,
            currency: 'USD',
            leadTime: { value: 5, unit: 'days' },
            criticalityLevel: 'critical',
            compatibleAssetTypes: ['iot_gateway'],
            failureModes: ['power_failure', 'overheat'],
            averageLifespan: { value: 5, unit: 'years' },
            specifications: { voltage: '12V', current: '2A', efficiency: '90%' },
          },
          {
            id: 'gateway_battery',
            partNumber: 'GW-BAT-001',
            name: 'Gateway Backup Battery',
            description: 'Lithium battery pack for backup power',
            category: 'gateway_components',
            manufacturer: 'Panasonic',
            unitCost: 45.00,
            currency: 'USD',
            leadTime: { value: 7, unit: 'days' },
            criticalityLevel: 'essential',
            compatibleAssetTypes: ['iot_gateway'],
            failureModes: ['capacity_loss', 'cell_failure'],
            averageLifespan: { value: 3, unit: 'years' },
            specifications: { capacity: '5000 mAh', voltage: '11.1V', runtime: '4 hours' },
          },
          {
            id: 'gateway_enclosure',
            partNumber: 'GW-ENC-001',
            name: 'Weatherproof Enclosure',
            description: 'IP67 rated outdoor enclosure for gateway',
            category: 'gateway_components',
            manufacturer: 'Polycase',
            unitCost: 75.00,
            currency: 'USD',
            leadTime: { value: 10, unit: 'days' },
            criticalityLevel: 'standard',
            compatibleAssetTypes: ['iot_gateway'],
            failureModes: ['seal_failure', 'physical_damage'],
            averageLifespan: { value: 10, unit: 'years' },
            specifications: { rating: 'IP67', material: 'Polycarbonate' },
          },
          {
            id: 'ethernet_cable',
            partNumber: 'GW-ETH-001',
            name: 'Industrial Ethernet Cable',
            description: 'Shielded Cat6 cable for gateway connections',
            category: 'gateway_components',
            manufacturer: 'Belden',
            unitCost: 15.00,
            currency: 'USD',
            leadTime: { value: 3, unit: 'days' },
            criticalityLevel: 'standard',
            compatibleAssetTypes: ['iot_gateway'],
            failureModes: ['cable_damage', 'connector_wear'],
            averageLifespan: { value: 10, unit: 'years' },
            specifications: { length: '5m', category: 'Cat6', shielding: 'STP' },
          },
          {
            id: 'din_rail_mount',
            partNumber: 'GW-DIN-001',
            name: 'DIN Rail Mount Kit',
            description: 'DIN rail mounting bracket for control panel installation',
            category: 'gateway_components',
            manufacturer: 'Phoenix Contact',
            unitCost: 8.00,
            currency: 'USD',
            leadTime: { value: 3, unit: 'days' },
            criticalityLevel: 'optional',
            compatibleAssetTypes: ['iot_gateway'],
            failureModes: ['mechanical_failure'],
            averageLifespan: { value: 15, unit: 'years' },
          },
        ],
      },
      {
        id: 'diagnostic_tools',
        name: 'Diagnostic Tools',
        description: 'Testing and diagnostic equipment',
        parts: [
          {
            id: 'signal_tester',
            partNumber: 'DIAG-SIG-001',
            name: 'Cellular Signal Tester',
            description: 'Handheld device for measuring RF signal parameters',
            category: 'diagnostic_tools',
            manufacturer: 'Anritsu',
            unitCost: 450.00,
            currency: 'USD',
            leadTime: { value: 14, unit: 'days' },
            criticalityLevel: 'standard',
            compatibleAssetTypes: ['sim_card', 'cellular_modem', 'iot_gateway'],
            failureModes: ['calibration_drift', 'battery_failure'],
            averageLifespan: { value: 7, unit: 'years' },
          },
          {
            id: 'sim_programmer',
            partNumber: 'DIAG-SIM-001',
            name: 'SIM Programmer/Reader',
            description: 'USB device for reading and programming SIM cards',
            category: 'diagnostic_tools',
            manufacturer: 'ACS',
            unitCost: 85.00,
            currency: 'USD',
            leadTime: { value: 7, unit: 'days' },
            criticalityLevel: 'standard',
            compatibleAssetTypes: ['sim_card', 'esim_profile'],
            failureModes: ['usb_failure', 'reader_wear'],
            averageLifespan: { value: 5, unit: 'years' },
          },
          {
            id: 'spectrum_analyzer',
            partNumber: 'DIAG-SPEC-001',
            name: 'Portable Spectrum Analyzer',
            description: 'RF spectrum analysis for interference detection',
            category: 'diagnostic_tools',
            manufacturer: 'Rohde & Schwarz',
            unitCost: 3500.00,
            currency: 'USD',
            leadTime: { value: 21, unit: 'days' },
            criticalityLevel: 'optional',
            compatibleAssetTypes: ['sim_card', 'cellular_modem', 'iot_gateway'],
            failureModes: ['calibration_drift'],
            averageLifespan: { value: 10, unit: 'years' },
          },
        ],
      },
      {
        id: 'consumables',
        name: 'Consumables',
        description: 'Consumable items and supplies',
        parts: [
          {
            id: 'cable_ties',
            partNumber: 'CON-TIE-001',
            name: 'UV-Resistant Cable Ties',
            description: 'Outdoor-rated cable ties for antenna installations',
            category: 'consumables',
            manufacturer: 'Generic',
            unitCost: 5.00,
            currency: 'USD',
            leadTime: { value: 2, unit: 'days' },
            criticalityLevel: 'optional',
            compatibleAssetTypes: ['iot_gateway'],
            failureModes: ['uv_degradation'],
            specifications: { quantity: '100 pack' },
          },
          {
            id: 'weatherproofing_tape',
            partNumber: 'CON-TAPE-001',
            name: 'Self-Amalgamating Tape',
            description: 'Weather sealing tape for outdoor connections',
            category: 'consumables',
            manufacturer: '3M',
            unitCost: 12.00,
            currency: 'USD',
            leadTime: { value: 3, unit: 'days' },
            criticalityLevel: 'standard',
            compatibleAssetTypes: ['iot_gateway'],
            failureModes: ['adhesive_failure'],
            specifications: { width: '19mm', length: '10m' },
          },
          {
            id: 'contact_cleaner',
            partNumber: 'CON-CLN-001',
            name: 'Electronic Contact Cleaner',
            description: 'Cleaning spray for SIM and antenna contacts',
            category: 'consumables',
            manufacturer: 'CRC',
            unitCost: 8.00,
            currency: 'USD',
            leadTime: { value: 2, unit: 'days' },
            criticalityLevel: 'optional',
            compatibleAssetTypes: ['sim_card', 'cellular_modem'],
            specifications: { volume: '300ml' },
          },
        ],
      },
    ],
  },

  rootCauseLibrary: {
    categories: [
      {
        id: 'signal_issues',
        name: 'Signal Issues',
        description: 'Problems related to RF signal quality',
        icon: 'Signal',
        color: '#f59e0b',
        rootCauses: [
          {
            id: 'weak_coverage',
            name: 'Weak Coverage Area',
            description: 'Device located in area with poor carrier coverage',
            category: 'signal_issues',
            symptoms: ['Low RSSI (<-90 dBm)', 'Frequent disconnections', 'Slow data speeds', 'High latency'],
            diagnosticSteps: [
              { order: 1, action: 'Check coverage map for device location', expectedResult: 'Identify expected coverage level', duration: 2, skillLevel: 'basic' },
              { order: 2, action: 'Compare RSSI across available carrier profiles', expectedResult: 'Find best carrier option', duration: 5, skillLevel: 'intermediate' },
              { order: 3, action: 'Test profile switch to alternative carrier', expectedResult: 'Improved signal or confirm no better option', duration: 5, skillLevel: 'intermediate' },
              { order: 4, action: 'Evaluate external antenna deployment', expectedResult: 'Determine feasibility of signal improvement', duration: 10, skillLevel: 'advanced' },
            ],
            preventiveActions: ['Deploy external antenna', 'Switch to carrier with better coverage', 'Consider device relocation', 'Implement multi-carrier fallback'],
            typicalProbability: 0.25,
            affectedComponents: ['sim_card', 'cellular_modem'],
            requiredExpertise: ['technician'],
            estimatedResolutionTime: { min: 15, typical: 30, max: 120, unit: 'minutes' },
            costRange: { min: 0, max: 500, currency: 'USD' },
          },
          {
            id: 'rf_interference',
            name: 'RF Interference',
            description: 'External interference degrading signal quality',
            category: 'signal_issues',
            symptoms: ['High SINR variance', 'Intermittent connectivity', 'Good RSSI but poor throughput', 'Retransmission spikes'],
            diagnosticSteps: [
              { order: 1, action: 'Run spectrum analysis at device location', expectedResult: 'Identify interference sources', duration: 15, skillLevel: 'advanced' },
              { order: 2, action: 'Check for nearby RF emitters', expectedResult: 'Document potential interference sources', duration: 10, skillLevel: 'intermediate' },
              { order: 3, action: 'Test different frequencies/bands', expectedResult: 'Find cleaner spectrum', duration: 10, skillLevel: 'advanced' },
            ],
            preventiveActions: ['Relocate device away from interference', 'Use directional antenna', 'Change operating frequency', 'Shield RF sources'],
            typicalProbability: 0.10,
            affectedComponents: ['sim_card', 'cellular_modem', 'iot_gateway'],
            requiredExpertise: ['technician', 'sme'],
            estimatedResolutionTime: { min: 30, typical: 60, max: 240, unit: 'minutes' },
            costRange: { min: 0, max: 1000, currency: 'USD' },
          },
          {
            id: 'antenna_problem',
            name: 'Antenna Problem',
            description: 'Faulty or improperly installed antenna',
            category: 'signal_issues',
            symptoms: ['Sudden signal drop', 'Asymmetric TX/RX power', 'No improvement with profile switch', 'Physical damage visible'],
            diagnosticSteps: [
              { order: 1, action: 'Inspect antenna and cable connections', expectedResult: 'Identify physical damage or loose connections', duration: 5, skillLevel: 'basic' },
              { order: 2, action: 'Test with known-good antenna', expectedResult: 'Confirm antenna as root cause', duration: 10, skillLevel: 'intermediate' },
              { order: 3, action: 'Check SWR/return loss', expectedResult: 'Verify antenna impedance match', duration: 10, skillLevel: 'advanced' },
            ],
            preventiveActions: ['Regular antenna inspections', 'Use quality connectors', 'Proper weatherproofing', 'Document installation specs'],
            typicalProbability: 0.08,
            affectedComponents: ['cellular_modem', 'iot_gateway'],
            requiredExpertise: ['technician'],
            estimatedResolutionTime: { min: 15, typical: 45, max: 120, unit: 'minutes' },
            costRange: { min: 45, max: 200, currency: 'USD' },
          },
        ],
      },
      {
        id: 'connectivity_issues',
        name: 'Connectivity Issues',
        description: 'Problems with network registration and data sessions',
        icon: 'Wifi',
        color: '#3b82f6',
        rootCauses: [
          {
            id: 'registration_failure',
            name: 'Network Registration Failure',
            description: 'SIM unable to register on cellular network',
            category: 'connectivity_issues',
            symptoms: ['No network connection', 'Registration rejected', 'IMSI unknown error', 'Authentication failure'],
            diagnosticSteps: [
              { order: 1, action: 'Check SIM status in management platform', expectedResult: 'Verify SIM is active and not suspended', duration: 2, skillLevel: 'basic' },
              { order: 2, action: 'Verify APN configuration', expectedResult: 'Confirm correct APN settings', duration: 3, skillLevel: 'intermediate' },
              { order: 3, action: 'Check carrier profile validity', expectedResult: 'Ensure profile not expired', duration: 2, skillLevel: 'intermediate' },
              { order: 4, action: 'Test with alternate profile', expectedResult: 'Isolate carrier vs SIM issue', duration: 5, skillLevel: 'intermediate' },
            ],
            preventiveActions: ['Monitor registration success rate', 'Implement automatic profile fallback', 'Keep profiles updated', 'Regular SIM health checks'],
            typicalProbability: 0.12,
            affectedComponents: ['sim_card', 'network_profile'],
            requiredExpertise: ['technician'],
            estimatedResolutionTime: { min: 5, typical: 15, max: 60, unit: 'minutes' },
            costRange: { min: 0, max: 100, currency: 'USD' },
          },
          {
            id: 'pdp_context_failure',
            name: 'PDP Context Activation Failure',
            description: 'Unable to establish data session',
            category: 'connectivity_issues',
            symptoms: ['Registered but no data', 'PDP activation rejected', 'No IP address assigned', 'APN error'],
            diagnosticSteps: [
              { order: 1, action: 'Verify APN credentials', expectedResult: 'Confirm username/password if required', duration: 2, skillLevel: 'basic' },
              { order: 2, action: 'Check data plan status', expectedResult: 'Verify data allowance not exhausted', duration: 2, skillLevel: 'basic' },
              { order: 3, action: 'Test alternate APN', expectedResult: 'Determine if APN-specific issue', duration: 5, skillLevel: 'intermediate' },
              { order: 4, action: 'Check GGSN/PGW status with carrier', expectedResult: 'Identify network-side issues', duration: 15, skillLevel: 'advanced' },
            ],
            preventiveActions: ['Verify APN configs before deployment', 'Monitor PDP activation success', 'Implement automatic retry logic'],
            typicalProbability: 0.08,
            affectedComponents: ['sim_card', 'network_profile'],
            requiredExpertise: ['technician'],
            estimatedResolutionTime: { min: 5, typical: 20, max: 60, unit: 'minutes' },
            costRange: { min: 0, max: 50, currency: 'USD' },
          },
          {
            id: 'handover_failure',
            name: 'Cell Handover Failure',
            description: 'Connection lost during cell tower transitions',
            category: 'connectivity_issues',
            symptoms: ['Drops during movement', 'Brief disconnections', 'Session resets', 'Location-specific issues'],
            diagnosticSteps: [
              { order: 1, action: 'Map failure locations', expectedResult: 'Identify problem cell boundaries', duration: 15, skillLevel: 'intermediate' },
              { order: 2, action: 'Check signal at transition zones', expectedResult: 'Verify adequate overlap coverage', duration: 10, skillLevel: 'intermediate' },
              { order: 3, action: 'Report to carrier if systematic', expectedResult: 'Network optimization request', duration: 10, skillLevel: 'advanced' },
            ],
            preventiveActions: ['Document known problem areas', 'Consider fixed installation in problem zones', 'Use carriers with better coverage'],
            typicalProbability: 0.05,
            affectedComponents: ['sim_card'],
            requiredExpertise: ['technician', 'sme'],
            estimatedResolutionTime: { min: 30, typical: 60, max: 240, unit: 'minutes' },
            costRange: { min: 0, max: 200, currency: 'USD' },
          },
        ],
      },
      {
        id: 'hardware_failures',
        name: 'Hardware Failures',
        description: 'Physical component failures',
        icon: 'Cpu',
        color: '#ef4444',
        rootCauses: [
          {
            id: 'sim_hardware_failure',
            name: 'SIM Card Hardware Failure',
            description: 'Physical SIM card malfunction',
            category: 'hardware_failures',
            symptoms: ['SIM not detected', 'Read errors', 'Intermittent detection', 'Profile corruption'],
            diagnosticSteps: [
              { order: 1, action: 'Reseat SIM card', expectedResult: 'Rule out contact issue', duration: 2, skillLevel: 'basic' },
              { order: 2, action: 'Clean SIM contacts', expectedResult: 'Remove corrosion or debris', duration: 3, skillLevel: 'basic' },
              { order: 3, action: 'Test in alternate device', expectedResult: 'Confirm SIM vs device issue', duration: 5, skillLevel: 'intermediate' },
              { order: 4, action: 'Check SIM diagnostics in platform', expectedResult: 'Identify reported errors', duration: 3, skillLevel: 'intermediate' },
            ],
            preventiveActions: ['Use quality SIM cards', 'Protect from environmental extremes', 'Regular diagnostics', 'Track SIM age'],
            typicalProbability: 0.03,
            affectedComponents: ['sim_card'],
            requiredExpertise: ['technician'],
            estimatedResolutionTime: { min: 10, typical: 30, max: 60, unit: 'minutes' },
            costRange: { min: 5, max: 50, currency: 'USD' },
          },
          {
            id: 'modem_failure',
            name: 'Cellular Modem Failure',
            description: 'Embedded modem hardware failure',
            category: 'hardware_failures',
            symptoms: ['No modem response', 'AT command failures', 'Thermal shutdown', 'Firmware corruption'],
            diagnosticSteps: [
              { order: 1, action: 'Power cycle device', expectedResult: 'Clear transient fault', duration: 2, skillLevel: 'basic' },
              { order: 2, action: 'Check modem temperature', expectedResult: 'Identify thermal issues', duration: 3, skillLevel: 'intermediate' },
              { order: 3, action: 'Attempt firmware recovery', expectedResult: 'Restore modem operation', duration: 15, skillLevel: 'advanced' },
              { order: 4, action: 'Test modem isolation', expectedResult: 'Confirm modem vs host issue', duration: 10, skillLevel: 'advanced' },
            ],
            preventiveActions: ['Monitor modem temperature', 'Ensure adequate ventilation', 'Keep firmware updated', 'Use industrial-rated modems'],
            typicalProbability: 0.02,
            affectedComponents: ['cellular_modem'],
            requiredExpertise: ['technician', 'sme'],
            estimatedResolutionTime: { min: 15, typical: 60, max: 180, unit: 'minutes' },
            costRange: { min: 100, max: 500, currency: 'USD' },
          },
          {
            id: 'gateway_failure',
            name: 'IoT Gateway Failure',
            description: 'Gateway hardware or software failure',
            category: 'hardware_failures',
            symptoms: ['Gateway unresponsive', 'No connected SIMs', 'Management disconnect', 'Boot loop'],
            diagnosticSteps: [
              { order: 1, action: 'Check power supply', expectedResult: 'Verify power delivery', duration: 3, skillLevel: 'basic' },
              { order: 2, action: 'Attempt remote reboot', expectedResult: 'Recover from software hang', duration: 5, skillLevel: 'basic' },
              { order: 3, action: 'Check status LEDs', expectedResult: 'Identify failure mode', duration: 2, skillLevel: 'intermediate' },
              { order: 4, action: 'Dispatch field technician', expectedResult: 'On-site diagnosis', duration: 60, skillLevel: 'intermediate' },
            ],
            preventiveActions: ['Implement watchdog timers', 'UPS/battery backup', 'Remote monitoring', 'Regular firmware updates'],
            typicalProbability: 0.02,
            affectedComponents: ['iot_gateway'],
            requiredExpertise: ['technician'],
            estimatedResolutionTime: { min: 30, typical: 120, max: 480, unit: 'minutes' },
            costRange: { min: 100, max: 2000, currency: 'USD' },
          },
          {
            id: 'power_failure',
            name: 'Power Supply Failure',
            description: 'Device power supply or battery failure',
            category: 'hardware_failures',
            symptoms: ['Device offline', 'Intermittent operation', 'Battery not charging', 'Brown-out restarts'],
            diagnosticSteps: [
              { order: 1, action: 'Check power source', expectedResult: 'Verify input power available', duration: 3, skillLevel: 'basic' },
              { order: 2, action: 'Measure voltage levels', expectedResult: 'Confirm voltage within spec', duration: 5, skillLevel: 'intermediate' },
              { order: 3, action: 'Test with alternate power supply', expectedResult: 'Isolate PSU vs device', duration: 10, skillLevel: 'intermediate' },
              { order: 4, action: 'Check battery health', expectedResult: 'Determine if replacement needed', duration: 5, skillLevel: 'intermediate' },
            ],
            preventiveActions: ['Use quality power supplies', 'Implement UPS', 'Monitor battery health', 'Track power supply age'],
            typicalProbability: 0.04,
            affectedComponents: ['iot_gateway', 'cellular_modem'],
            requiredExpertise: ['technician'],
            estimatedResolutionTime: { min: 15, typical: 45, max: 120, unit: 'minutes' },
            costRange: { min: 25, max: 150, currency: 'USD' },
          },
        ],
      },
      {
        id: 'configuration_issues',
        name: 'Configuration Issues',
        description: 'Software and profile configuration problems',
        icon: 'Settings',
        color: '#8b5cf6',
        rootCauses: [
          {
            id: 'apn_misconfiguration',
            name: 'APN Misconfiguration',
            description: 'Incorrect Access Point Name settings',
            category: 'configuration_issues',
            symptoms: ['Connected but no data', 'Authentication errors', 'Wrong IP assignment', 'Roaming issues'],
            diagnosticSteps: [
              { order: 1, action: 'Review current APN settings', expectedResult: 'Document current configuration', duration: 2, skillLevel: 'basic' },
              { order: 2, action: 'Compare with carrier specifications', expectedResult: 'Identify discrepancies', duration: 3, skillLevel: 'intermediate' },
              { order: 3, action: 'Apply correct APN settings', expectedResult: 'Data connectivity restored', duration: 5, skillLevel: 'intermediate' },
              { order: 4, action: 'Test across multiple locations', expectedResult: 'Verify consistent operation', duration: 15, skillLevel: 'intermediate' },
            ],
            preventiveActions: ['Validate APNs before deployment', 'Document carrier requirements', 'Automated APN provisioning', 'Version control configs'],
            typicalProbability: 0.10,
            affectedComponents: ['network_profile'],
            requiredExpertise: ['technician'],
            estimatedResolutionTime: { min: 5, typical: 15, max: 30, unit: 'minutes' },
            costRange: { min: 0, max: 25, currency: 'USD' },
          },
          {
            id: 'profile_mismatch',
            name: 'Profile Mismatch',
            description: 'Carrier profile incompatible with region or device',
            category: 'configuration_issues',
            symptoms: ['No service in specific regions', 'Roaming failures', 'Limited band support', 'Unexpected charges'],
            diagnosticSteps: [
              { order: 1, action: 'Check device location vs profile coverage', expectedResult: 'Confirm profile supports region', duration: 3, skillLevel: 'intermediate' },
              { order: 2, action: 'Verify device band support', expectedResult: 'Ensure device supports carrier bands', duration: 5, skillLevel: 'intermediate' },
              { order: 3, action: 'Switch to appropriate profile', expectedResult: 'Connectivity restored', duration: 5, skillLevel: 'intermediate' },
            ],
            preventiveActions: ['Pre-validate profiles for deployment regions', 'Maintain regional profile inventory', 'Automated profile switching'],
            typicalProbability: 0.08,
            affectedComponents: ['network_profile', 'esim_profile'],
            requiredExpertise: ['technician', 'sme'],
            estimatedResolutionTime: { min: 5, typical: 20, max: 60, unit: 'minutes' },
            costRange: { min: 0, max: 100, currency: 'USD' },
          },
          {
            id: 'firmware_incompatibility',
            name: 'Firmware Incompatibility',
            description: 'Device firmware incompatible with network or profiles',
            category: 'configuration_issues',
            symptoms: ['Features not working', 'Stability issues after update', 'Profile download failures', 'Reduced performance'],
            diagnosticSteps: [
              { order: 1, action: 'Check current firmware version', expectedResult: 'Document installed version', duration: 2, skillLevel: 'basic' },
              { order: 2, action: 'Compare with recommended versions', expectedResult: 'Identify version mismatch', duration: 3, skillLevel: 'intermediate' },
              { order: 3, action: 'Review firmware release notes', expectedResult: 'Check for known issues', duration: 5, skillLevel: 'intermediate' },
              { order: 4, action: 'Update or rollback firmware', expectedResult: 'Restore compatibility', duration: 15, skillLevel: 'advanced' },
            ],
            preventiveActions: ['Test firmware before fleet rollout', 'Maintain firmware version database', 'Staged rollouts', 'Rollback capability'],
            typicalProbability: 0.05,
            affectedComponents: ['cellular_modem', 'iot_gateway'],
            requiredExpertise: ['technician', 'sme'],
            estimatedResolutionTime: { min: 15, typical: 30, max: 90, unit: 'minutes' },
            costRange: { min: 0, max: 100, currency: 'USD' },
          },
        ],
      },
      {
        id: 'performance_issues',
        name: 'Performance Issues',
        description: 'Degraded network performance without complete failure',
        icon: 'TrendingDown',
        color: '#f97316',
        rootCauses: [
          {
            id: 'network_congestion',
            name: 'Network Congestion',
            description: 'Carrier network overloaded causing performance degradation',
            category: 'performance_issues',
            symptoms: ['High latency', 'Low throughput', 'Time-of-day patterns', 'Packet loss spikes'],
            diagnosticSteps: [
              { order: 1, action: 'Analyze performance over time', expectedResult: 'Identify congestion patterns', duration: 10, skillLevel: 'intermediate' },
              { order: 2, action: 'Compare with alternate carriers', expectedResult: 'Determine if carrier-specific', duration: 15, skillLevel: 'intermediate' },
              { order: 3, action: 'Contact carrier support', expectedResult: 'Report and escalate issue', duration: 20, skillLevel: 'advanced' },
            ],
            preventiveActions: ['Multi-carrier redundancy', 'Traffic shaping', 'Off-peak scheduling', 'Monitor congestion trends'],
            typicalProbability: 0.12,
            affectedComponents: ['sim_card', 'network_profile'],
            requiredExpertise: ['sme'],
            estimatedResolutionTime: { min: 30, typical: 120, max: 480, unit: 'minutes' },
            costRange: { min: 0, max: 500, currency: 'USD' },
          },
          {
            id: 'throttling',
            name: 'Data Throttling',
            description: 'Carrier throttling data speed after quota threshold',
            category: 'performance_issues',
            symptoms: ['Sudden speed drop', 'Consistent slow speeds', 'Quota threshold reached', 'Policy notification'],
            diagnosticSteps: [
              { order: 1, action: 'Check data usage vs quota', expectedResult: 'Identify if threshold exceeded', duration: 2, skillLevel: 'basic' },
              { order: 2, action: 'Review carrier throttling policy', expectedResult: 'Understand throttle conditions', duration: 5, skillLevel: 'intermediate' },
              { order: 3, action: 'Consider plan upgrade or profile switch', expectedResult: 'Determine resolution options', duration: 10, skillLevel: 'sme' },
            ],
            preventiveActions: ['Monitor usage trends', 'Set quota alerts', 'Right-size data plans', 'Implement usage optimization'],
            typicalProbability: 0.08,
            affectedComponents: ['sim_card', 'network_profile'],
            requiredExpertise: ['technician', 'sme'],
            estimatedResolutionTime: { min: 5, typical: 30, max: 60, unit: 'minutes' },
            costRange: { min: 0, max: 200, currency: 'USD' },
          },
        ],
      },
      {
        id: 'capacity_issues',
        name: 'Capacity Issues',
        description: 'Data quota and billing-related issues',
        icon: 'AlertCircle',
        color: '#eab308',
        rootCauses: [
          {
            id: 'quota_exceeded',
            name: 'Data Quota Exceeded',
            description: 'Monthly data allowance depleted',
            category: 'capacity_issues',
            symptoms: ['No data connectivity', 'Overage charges', 'Service suspension', 'Policy notification'],
            diagnosticSteps: [
              { order: 1, action: 'Check current usage in platform', expectedResult: 'Confirm quota status', duration: 2, skillLevel: 'basic' },
              { order: 2, action: 'Review usage pattern', expectedResult: 'Identify unusual consumption', duration: 5, skillLevel: 'intermediate' },
              { order: 3, action: 'Add data top-up or upgrade plan', expectedResult: 'Restore connectivity', duration: 5, skillLevel: 'basic' },
            ],
            preventiveActions: ['Set usage alerts at 70%/90%', 'Implement data pooling', 'Optimize device data usage', 'Regular usage reviews'],
            typicalProbability: 0.08,
            affectedComponents: ['sim_card'],
            requiredExpertise: ['technician'],
            estimatedResolutionTime: { min: 5, typical: 15, max: 30, unit: 'minutes' },
            costRange: { min: 10, max: 500, currency: 'USD' },
          },
          {
            id: 'billing_suspension',
            name: 'Billing Suspension',
            description: 'Service suspended due to payment or billing issues',
            category: 'capacity_issues',
            symptoms: ['Complete service loss', 'Billing notification', 'Account suspension', 'Multiple SIMs affected'],
            diagnosticSteps: [
              { order: 1, action: 'Check account billing status', expectedResult: 'Identify billing issue', duration: 3, skillLevel: 'basic' },
              { order: 2, action: 'Contact carrier billing support', expectedResult: 'Resolve billing dispute', duration: 30, skillLevel: 'sme' },
              { order: 3, action: 'Process payment if required', expectedResult: 'Clear payment hold', duration: 15, skillLevel: 'basic' },
            ],
            preventiveActions: ['Autopay enrollment', 'Monitor billing alerts', 'Maintain payment methods', 'Regular billing reviews'],
            typicalProbability: 0.02,
            affectedComponents: ['sim_card', 'network_profile'],
            requiredExpertise: ['sme'],
            estimatedResolutionTime: { min: 30, typical: 120, max: 480, unit: 'minutes' },
            costRange: { min: 0, max: 1000, currency: 'USD' },
          },
        ],
      },
      {
        id: 'security_issues',
        name: 'Security Issues',
        description: 'Security-related incidents and fraud',
        icon: 'Shield',
        color: '#dc2626',
        rootCauses: [
          {
            id: 'sim_fraud',
            name: 'SIM Fraud/Cloning',
            description: 'Unauthorized use or cloning of SIM credentials',
            category: 'security_issues',
            symptoms: ['Unexpected usage', 'Multiple concurrent sessions', 'Unusual locations', 'Billing anomalies'],
            diagnosticSteps: [
              { order: 1, action: 'Review session logs', expectedResult: 'Identify unauthorized access', duration: 10, skillLevel: 'intermediate' },
              { order: 2, action: 'Check concurrent session count', expectedResult: 'Detect cloning', duration: 5, skillLevel: 'intermediate' },
              { order: 3, action: 'Suspend compromised SIM', expectedResult: 'Stop unauthorized use', duration: 2, skillLevel: 'basic' },
              { order: 4, action: 'Issue replacement SIM with new credentials', expectedResult: 'Restore secure service', duration: 30, skillLevel: 'intermediate' },
            ],
            preventiveActions: ['Monitor for anomalous activity', 'Implement velocity checks', 'Regular security audits', 'Use eUICC with secure element'],
            typicalProbability: 0.01,
            affectedComponents: ['sim_card'],
            requiredExpertise: ['sme', 'executive'],
            estimatedResolutionTime: { min: 30, typical: 120, max: 480, unit: 'minutes' },
            costRange: { min: 50, max: 5000, currency: 'USD' },
          },
          {
            id: 'unauthorized_access',
            name: 'Unauthorized Platform Access',
            description: 'Unauthorized access to SIM management platform',
            category: 'security_issues',
            symptoms: ['Unexpected config changes', 'Mass profile switches', 'Unusual API activity', 'Account alerts'],
            diagnosticSteps: [
              { order: 1, action: 'Review access logs', expectedResult: 'Identify unauthorized sessions', duration: 10, skillLevel: 'intermediate' },
              { order: 2, action: 'Reset compromised credentials', expectedResult: 'Block unauthorized access', duration: 5, skillLevel: 'basic' },
              { order: 3, action: 'Audit recent changes', expectedResult: 'Identify scope of impact', duration: 30, skillLevel: 'advanced' },
              { order: 4, action: 'Implement security improvements', expectedResult: 'Prevent recurrence', duration: 60, skillLevel: 'advanced' },
            ],
            preventiveActions: ['MFA for all accounts', 'Regular credential rotation', 'Access logging', 'Role-based access control'],
            typicalProbability: 0.005,
            affectedComponents: ['sim_card', 'network_profile', 'iot_gateway'],
            requiredExpertise: ['sme', 'executive'],
            estimatedResolutionTime: { min: 60, typical: 240, max: 720, unit: 'minutes' },
            costRange: { min: 100, max: 10000, currency: 'USD' },
          },
        ],
      },
      {
        id: 'regulatory_issues',
        name: 'Regulatory Issues',
        description: 'Compliance and regulatory violations',
        icon: 'FileWarning',
        color: '#7c3aed',
        rootCauses: [
          {
            id: 'local_imsi_violation',
            name: 'Local IMSI Requirement Violation',
            description: 'Operating without required local IMSI in regulated region',
            category: 'regulatory_issues',
            symptoms: ['Service blocked by carrier', 'Regulatory notification', 'Roaming restrictions', 'Compliance alerts'],
            diagnosticSteps: [
              { order: 1, action: 'Verify device location', expectedResult: 'Confirm operating region', duration: 3, skillLevel: 'basic' },
              { order: 2, action: 'Check local IMSI requirements', expectedResult: 'Understand compliance needs', duration: 10, skillLevel: 'intermediate' },
              { order: 3, action: 'Provision compliant profile', expectedResult: 'Enable local IMSI', duration: 15, skillLevel: 'intermediate' },
            ],
            preventiveActions: ['Pre-validate regional requirements', 'Automated compliance checking', 'Regular regulatory updates', 'Geofencing alerts'],
            typicalProbability: 0.05,
            affectedComponents: ['sim_card', 'network_profile'],
            requiredExpertise: ['sme', 'executive'],
            estimatedResolutionTime: { min: 30, typical: 120, max: 480, unit: 'minutes' },
            costRange: { min: 0, max: 2000, currency: 'USD' },
          },
          {
            id: 'data_residency_violation',
            name: 'Data Residency Violation',
            description: 'Data routed through non-compliant regions',
            category: 'regulatory_issues',
            symptoms: ['GDPR alerts', 'Data sovereignty warnings', 'Audit findings', 'Carrier notifications'],
            diagnosticSteps: [
              { order: 1, action: 'Audit data routing paths', expectedResult: 'Identify non-compliant routes', duration: 30, skillLevel: 'advanced' },
              { order: 2, action: 'Review carrier data handling', expectedResult: 'Verify carrier compliance', duration: 20, skillLevel: 'sme' },
              { order: 3, action: 'Implement compliant routing', expectedResult: 'Ensure data stays in region', duration: 60, skillLevel: 'advanced' },
            ],
            preventiveActions: ['Select compliant carriers', 'Document data flows', 'Regular compliance audits', 'Data minimization'],
            typicalProbability: 0.03,
            affectedComponents: ['network_profile'],
            requiredExpertise: ['sme', 'executive'],
            estimatedResolutionTime: { min: 60, typical: 480, max: 1440, unit: 'minutes' },
            costRange: { min: 500, max: 50000, currency: 'USD' },
          },
        ],
      },
    ],
    commonPatterns: [
      {
        id: 'signal_degradation_pattern',
        name: 'Progressive Signal Degradation',
        description: 'Gradual signal weakening leading to disconnection',
        sequence: ['RSSI drop below -85 dBm', 'Increased retransmissions', 'Latency increase >100ms', 'Connection timeout', 'Registration failure'],
        indicators: ['RSSI trending down over 24h', 'Latency increasing', 'Packet loss rising'],
        preventionStrategy: 'Proactive profile switching when RSSI drops below -80 dBm; consider antenna optimization',
      },
      {
        id: 'quota_exhaustion_pattern',
        name: 'Data Quota Exhaustion',
        description: 'Predictable quota depletion leading to service disruption',
        sequence: ['Usage >70% of quota', 'Usage acceleration detected', 'Usage >90% alert', 'Quota exceeded', 'Service throttled or suspended'],
        indicators: ['Usage rate above average', 'Days remaining vs quota remaining mismatch', 'Unusual consumption spike'],
        preventionStrategy: 'Set alerts at 70% and 90% thresholds; implement data optimization; consider plan upgrade',
      },
      {
        id: 'hardware_degradation_pattern',
        name: 'Hardware Degradation',
        description: 'Gradual hardware failure with increasing symptoms',
        sequence: ['Intermittent errors logged', 'Error frequency increasing', 'Performance degradation', 'Partial functionality loss', 'Complete failure'],
        indicators: ['Error rate trending up', 'Temperature anomalies', 'Response time increasing'],
        preventionStrategy: 'Monitor error rates and proactively replace components when threshold exceeded',
      },
      {
        id: 'configuration_drift_pattern',
        name: 'Configuration Drift',
        description: 'Gradual configuration issues from unmanaged changes',
        sequence: ['Manual config change made', 'Change not documented', 'Incompatibility introduced', 'Partial functionality issues', 'Complete service impact'],
        indicators: ['Config version mismatch', 'Unexpected settings', 'Deployment variation'],
        preventionStrategy: 'Use configuration management; version control all settings; automated compliance checking',
      },
      {
        id: 'carrier_failover_pattern',
        name: 'Carrier Failover Cascade',
        description: 'Primary carrier failure triggering failover sequence',
        sequence: ['Primary carrier signal loss', 'Failover to secondary initiated', 'Secondary registration attempt', 'If secondary fails, tertiary tried', 'Multi-carrier exhaustion'],
        indicators: ['Primary RSSI dropping', 'Registration failures', 'Profile switch events'],
        preventionStrategy: 'Maintain multiple carrier profiles; regular failover testing; pre-validated backup carriers',
      },
      {
        id: 'security_breach_pattern',
        name: 'Security Incident Escalation',
        description: 'Security compromise with escalating impact',
        sequence: ['Anomalous activity detected', 'Unauthorized access confirmed', 'Data or service compromise', 'Lateral movement attempt', 'Full breach'],
        indicators: ['Unusual login locations', 'Concurrent sessions', 'Mass configuration changes', 'API abuse patterns'],
        preventionStrategy: 'Real-time anomaly detection; automated suspension; MFA enforcement; regular security audits',
      },
    ],
  },

  laborRates: {
    roles: [
      { roleId: 'noc_engineer', name: 'NOC Engineer', hourlyRate: 75, currency: 'USD', certifications: ['CCNA', 'IoT Specialist', 'AWS IoT'] },
      { roleId: 'field_technician', name: 'Field Technician', hourlyRate: 50, currency: 'USD', certifications: ['RF Fundamentals', 'Field Service'] },
      { roleId: 'fleet_manager', name: 'Fleet Manager', hourlyRate: 100, currency: 'USD', certifications: ['PMP', 'IoT Solutions Architect'] },
      { roleId: 'compliance_analyst', name: 'Compliance Analyst', hourlyRate: 85, currency: 'USD', certifications: ['GDPR Practitioner', 'Telecom Regulatory'] },
      { roleId: 'rf_engineer', name: 'RF Engineer', hourlyRate: 95, currency: 'USD', certifications: ['CWNA', 'RF Engineering'] },
    ],
    overtimeMultiplier: 1.5,
    emergencyMultiplier: 2.0,
    weekendMultiplier: 1.25,
  },

  visualization: {
    primaryColor: '#6366f1',
    secondaryColor: '#8b5cf6',
    healthyColor: '#22c55e',
    warningColor: '#f59e0b',
    criticalColor: '#ef4444',
    chartDefaults: {
      showGrid: true,
      showLegend: true,
      animationDuration: 300,
    },
  },

  narrativeTemplates: [
    // Fleet Health Narratives
    {
      id: 'fleet_health_summary',
      name: 'Fleet Health Summary',
      context: 'asset_health',
      audience: ['executive', 'manager'],
      template: 'Your SIM fleet of {total_count} devices is currently {health_status}. {connected_count} SIMs ({connected_percent}%) are online with an average signal score of {avg_signal}%. {alert_count} SIMs require attention: {critical_count} are offline, and {degraded_count} have weak signal. Primary concern: {primary_issue}.',
      variables: [
        { name: 'total_count', type: 'metric' },
        { name: 'health_status', type: 'metric' },
        { name: 'connected_count', type: 'metric' },
        { name: 'connected_percent', type: 'percentage' },
        { name: 'avg_signal', type: 'percentage' },
        { name: 'alert_count', type: 'metric' },
        { name: 'critical_count', type: 'metric' },
        { name: 'degraded_count', type: 'metric' },
        { name: 'primary_issue', type: 'metric' },
      ],
    },
    {
      id: 'fleet_health_technical',
      name: 'Fleet Health Technical Detail',
      context: 'asset_health',
      audience: ['engineer', 'technician'],
      template: 'Fleet Status: {connected_count}/{total_count} SIMs online. Signal metrics - Average RSSI: {avg_rssi} dBm, Average RSRP: {avg_rsrp} dBm, Average latency: {avg_latency}ms. {offline_count} devices offline requiring diagnostic. {weak_signal_count} devices with RSSI below -85 dBm may need profile optimization or antenna review. Top affected region: {problem_region}.',
      variables: [
        { name: 'connected_count', type: 'metric' },
        { name: 'total_count', type: 'metric' },
        { name: 'avg_rssi', type: 'metric' },
        { name: 'avg_rsrp', type: 'metric' },
        { name: 'avg_latency', type: 'metric' },
        { name: 'offline_count', type: 'metric' },
        { name: 'weak_signal_count', type: 'metric' },
        { name: 'problem_region', type: 'metric' },
      ],
    },
    // Anomaly Narratives
    {
      id: 'network_anomaly_alert',
      name: 'Network Anomaly Alert',
      context: 'anomaly',
      audience: ['engineer', 'technician'],
      template: 'Network anomaly detected on SIM {asset_id} ({asset_name}). Signal dropped from {previous_rssi} dBm to {current_rssi} dBm over {time_period}. Current carrier: {carrier_name}. Anomaly confidence: {confidence}%. Recommended action: {recommendation}. Similar pattern seen in {similar_cases} other SIMs in the past 7 days.',
      variables: [
        { name: 'asset_id', type: 'asset' },
        { name: 'asset_name', type: 'asset' },
        { name: 'previous_rssi', type: 'metric' },
        { name: 'current_rssi', type: 'metric' },
        { name: 'time_period', type: 'time' },
        { name: 'carrier_name', type: 'metric' },
        { name: 'confidence', type: 'percentage' },
        { name: 'recommendation', type: 'metric' },
        { name: 'similar_cases', type: 'metric' },
      ],
    },
    {
      id: 'usage_anomaly_alert',
      name: 'Data Usage Anomaly',
      context: 'anomaly',
      audience: ['manager', 'analyst'],
      template: 'Unusual data consumption detected on SIM {asset_id}. Current usage: {current_usage} MB vs. expected: {expected_usage} MB ({deviation}% deviation). Daily rate: {daily_rate} MB/day. At current rate, quota will be exhausted in {days_remaining} days. Potential causes: {suspected_cause}. Immediate action: {recommendation}.',
      variables: [
        { name: 'asset_id', type: 'asset' },
        { name: 'current_usage', type: 'metric' },
        { name: 'expected_usage', type: 'metric' },
        { name: 'deviation', type: 'percentage' },
        { name: 'daily_rate', type: 'metric' },
        { name: 'days_remaining', type: 'metric' },
        { name: 'suspected_cause', type: 'metric' },
        { name: 'recommendation', type: 'metric' },
      ],
    },
    // Prediction Narratives
    {
      id: 'connectivity_forecast',
      name: 'Connectivity Forecast',
      context: 'prediction',
      audience: ['manager', 'engineer'],
      template: 'Connectivity forecast for next {forecast_period}: {at_risk_count} SIMs predicted to experience connectivity issues. High-risk SIMs: {high_risk_list}. Primary risk factors: Signal degradation ({signal_risk_percent}%), carrier congestion ({congestion_risk_percent}%), quota exhaustion ({quota_risk_percent}%). Recommended preemptive actions: {recommendations}.',
      variables: [
        { name: 'forecast_period', type: 'time' },
        { name: 'at_risk_count', type: 'metric' },
        { name: 'high_risk_list', type: 'metric' },
        { name: 'signal_risk_percent', type: 'percentage' },
        { name: 'congestion_risk_percent', type: 'percentage' },
        { name: 'quota_risk_percent', type: 'percentage' },
        { name: 'recommendations', type: 'metric' },
      ],
    },
    {
      id: 'cost_forecast',
      name: 'Cost Forecast',
      context: 'prediction',
      audience: ['executive', 'manager'],
      template: 'Monthly cost projection: ${projected_cost} (current run rate). Budget utilization: {budget_percent}% of ${monthly_budget}. {overage_risk} overage risk detected. Top cost drivers: {cost_driver_1} (${cost_1}), {cost_driver_2} (${cost_2}). Optimization opportunities: {savings_opportunity} in potential monthly savings through {optimization_action}.',
      variables: [
        { name: 'projected_cost', type: 'currency' },
        { name: 'budget_percent', type: 'percentage' },
        { name: 'monthly_budget', type: 'currency' },
        { name: 'overage_risk', type: 'metric' },
        { name: 'cost_driver_1', type: 'metric' },
        { name: 'cost_1', type: 'currency' },
        { name: 'cost_driver_2', type: 'metric' },
        { name: 'cost_2', type: 'currency' },
        { name: 'savings_opportunity', type: 'currency' },
        { name: 'optimization_action', type: 'metric' },
      ],
    },
    // Maintenance Narratives
    {
      id: 'profile_management_summary',
      name: 'Profile Management Summary',
      context: 'maintenance',
      audience: ['engineer', 'technician'],
      template: 'Profile management status: {pending_switches} profile switches pending, {completed_switches} completed today. OTA updates: {ota_pending} devices awaiting update, {ota_failed} failed (retry scheduled). Firmware status: {outdated_firmware_count} gateways running outdated firmware. Priority actions: {priority_actions}.',
      variables: [
        { name: 'pending_switches', type: 'metric' },
        { name: 'completed_switches', type: 'metric' },
        { name: 'ota_pending', type: 'metric' },
        { name: 'ota_failed', type: 'metric' },
        { name: 'outdated_firmware_count', type: 'metric' },
        { name: 'priority_actions', type: 'metric' },
      ],
    },
    {
      id: 'field_service_summary',
      name: 'Field Service Summary',
      context: 'maintenance',
      audience: ['manager', 'technician'],
      template: 'Field service summary: {open_tickets} open network tickets, {urgent_tickets} marked urgent. SIM replacements needed: {sim_replacements}. Antenna installations pending: {antenna_installs}. Average resolution time: {avg_resolution} hours. Estimated field work: {field_hours} technician hours this week. Cost estimate: ${estimated_cost}.',
      variables: [
        { name: 'open_tickets', type: 'metric' },
        { name: 'urgent_tickets', type: 'metric' },
        { name: 'sim_replacements', type: 'metric' },
        { name: 'antenna_installs', type: 'metric' },
        { name: 'avg_resolution', type: 'metric' },
        { name: 'field_hours', type: 'metric' },
        { name: 'estimated_cost', type: 'currency' },
      ],
    },
    // Trend Narratives
    {
      id: 'signal_trend_analysis',
      name: 'Signal Quality Trend',
      context: 'trend',
      audience: ['engineer', 'analyst'],
      template: 'Signal quality trend ({trend_period}): Fleet average RSSI {rssi_trend} from {rssi_start} dBm to {rssi_current} dBm. {improving_count} SIMs showing improvement, {degrading_count} showing degradation. Regional analysis: {best_region} performing best (+{best_improvement} dB), {worst_region} needs attention ({worst_decline} dB decline). Carrier performance: {best_carrier} leading with {carrier_score}% reliability.',
      variables: [
        { name: 'trend_period', type: 'time' },
        { name: 'rssi_trend', type: 'metric' },
        { name: 'rssi_start', type: 'metric' },
        { name: 'rssi_current', type: 'metric' },
        { name: 'improving_count', type: 'metric' },
        { name: 'degrading_count', type: 'metric' },
        { name: 'best_region', type: 'metric' },
        { name: 'best_improvement', type: 'metric' },
        { name: 'worst_region', type: 'metric' },
        { name: 'worst_decline', type: 'metric' },
        { name: 'best_carrier', type: 'metric' },
        { name: 'carrier_score', type: 'percentage' },
      ],
    },
    // Comparison Narratives
    {
      id: 'carrier_comparison',
      name: 'Carrier Comparison',
      context: 'comparison',
      audience: ['manager', 'executive'],
      template: 'Carrier performance comparison: {carrier_1} vs {carrier_2} in {region}. Signal: {carrier_1} averages {c1_rssi} dBm vs {carrier_2} at {c2_rssi} dBm. Uptime: {c1_uptime}% vs {c2_uptime}%. Cost per GB: ${c1_cost} vs ${c2_cost}. Recommendation: {recommendation} based on {decision_factor}.',
      variables: [
        { name: 'carrier_1', type: 'metric' },
        { name: 'carrier_2', type: 'metric' },
        { name: 'region', type: 'metric' },
        { name: 'c1_rssi', type: 'metric' },
        { name: 'c2_rssi', type: 'metric' },
        { name: 'c1_uptime', type: 'percentage' },
        { name: 'c2_uptime', type: 'percentage' },
        { name: 'c1_cost', type: 'currency' },
        { name: 'c2_cost', type: 'currency' },
        { name: 'recommendation', type: 'metric' },
        { name: 'decision_factor', type: 'metric' },
      ],
    },
    // Executive Summary
    {
      id: 'executive_summary',
      name: 'Executive Summary',
      context: 'asset_health',
      audience: ['executive'],
      template: 'IoT Fleet Executive Summary: {total_sims} active SIMs across {region_count} regions. Fleet health: {health_status} ({health_score}% score). Monthly connectivity cost: ${monthly_cost} ({cost_trend} from last month). Key metrics: {uptime_percent}% uptime, {avg_latency}ms latency. Top priority: {top_priority}. Business impact: {business_impact}.',
      variables: [
        { name: 'total_sims', type: 'metric' },
        { name: 'region_count', type: 'metric' },
        { name: 'health_status', type: 'metric' },
        { name: 'health_score', type: 'percentage' },
        { name: 'monthly_cost', type: 'currency' },
        { name: 'cost_trend', type: 'metric' },
        { name: 'uptime_percent', type: 'percentage' },
        { name: 'avg_latency', type: 'metric' },
        { name: 'top_priority', type: 'metric' },
        { name: 'business_impact', type: 'metric' },
      ],
    },
    // Compliance Narrative
    {
      id: 'compliance_status',
      name: 'Compliance Status',
      context: 'asset_health',
      audience: ['executive', 'analyst'],
      template: 'Regulatory compliance status: {compliant_count}/{total_count} SIMs fully compliant ({compliance_percent}%). Regions requiring attention: {non_compliant_regions}. Issues: {issue_count} local IMSI violations, {data_issues} data residency concerns. Next audit: {next_audit_date}. Remediation timeline: {remediation_timeline}.',
      variables: [
        { name: 'compliant_count', type: 'metric' },
        { name: 'total_count', type: 'metric' },
        { name: 'compliance_percent', type: 'percentage' },
        { name: 'non_compliant_regions', type: 'metric' },
        { name: 'issue_count', type: 'metric' },
        { name: 'data_issues', type: 'metric' },
        { name: 'next_audit_date', type: 'time' },
        { name: 'remediation_timeline', type: 'metric' },
      ],
    },
  ],

  features: {
    enableBenchmarking: true,
    enableWhatIf: true,
    enableCollaboration: true,
    enableCompliance: true,
    enableKnowledgeCapture: true,
  },

  defaults: {
    timeRange: '24h',
    refreshInterval: 30,
    defaultDashboard: 'fleet_overview',
  },
}
